import difference from 'lodash/difference';

import type { NavItemConfigInternal, Store, UserMenuData, UserMenuDataShapes } from '../../types';
import { getMenuIdsByVisible } from '../get-menu-ids-by-visible';
import { repairGlobalMenuItems } from '../repair-global-menu-items';
import { repairItems } from '../repair-items';

type BuildUserMenuDataShapesParams = {
	allItemMenuIds: string[];
	allShortcutMenuIds: string[];
	forYouMenuId: string;
	globalItems: Map<string, NavItemConfigInternal>;
	rawUserMenuData: UserMenuData | null | undefined;
	setUserMenuData: Store['setUserMenuData'];
};

// Build common shapes out of user menu data.
// We do it here, once, so components don't perform the same filtering and mapping in more than one place.
export function buildUserMenuDataShapes(params: BuildUserMenuDataShapesParams): UserMenuDataShapes {
	const {
		allItemMenuIds,
		allShortcutMenuIds,
		forYouMenuId,
		globalItems,
		rawUserMenuData,
		setUserMenuData,
	} = params;

	// Remove any menu ids that overlap with shortcuts
	const distinctDefaultItemMenuIds = difference(allItemMenuIds, allShortcutMenuIds);

	const repairedUserMenuData = repairGlobalMenuItems({
		forYouMenuId,
		globalItems,
		rawUserMenuData,
	});

	// Add missing items and remove obsolete or duplicate items
	const itemUserMenuData = repair(repairedUserMenuData, distinctDefaultItemMenuIds);

	// Add missing shortcuts and remove obsolete or duplicate shortcuts
	const shortcutUserMenuData = repair(repairedUserMenuData, allShortcutMenuIds);

	// This repaired user menu data should always be used instead of the raw user menu data
	const userMenuData = itemUserMenuData.concat(shortcutUserMenuData);

	const [visibleItemMenuIds, hiddenItemMenuIds] = getMenuIdsByVisible(itemUserMenuData);
	const [visibleShortcutMenuIds, hiddenShortcutMenuIds] = getMenuIdsByVisible(shortcutUserMenuData);
	const visibleMenuIds = visibleItemMenuIds.concat(visibleShortcutMenuIds);
	const hiddenMenuIds = hiddenItemMenuIds.concat(hiddenShortcutMenuIds);

	return {
		hiddenItemMenuIds,
		hiddenMenuIds,
		hiddenShortcutMenuIds,
		itemUserMenuData,
		shortcutUserMenuData,
		setUserMenuData,
		userMenuData,
		visibleItemMenuIds,
		visibleMenuIds,
		visibleShortcutMenuIds,
	};
}

const repair = (rawUserMenuData: UserMenuData | null | undefined, defaultMenuIds: string[]) =>
	repairItems(rawUserMenuData ?? [], createDefaultItems(defaultMenuIds));

const createDefaultItems = (menuIds: string[]): UserMenuData =>
	menuIds.map((menuId) => ({ menuId, visible: true }));
